import { useEffect, useMemo, useState } from "react";
import api, { fmtMXN, fmtMXNFull } from "@/lib/api";
import { ChevronLeft, ChevronRight, CalendarDays, User, Users, Banknote, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import TeamWorkdays from "@/components/TeamWorkdays";

const DAY_TYPES = [
  { value: "completo", label: "Día completo", short: "C", bg: "rgba(34,197,94,0.28)", border: "var(--green)", color: "#86efac" },
  { value: "medio", label: "Medio día", short: "½", bg: "rgba(251,191,36,0.25)", border: "#fbbf24", color: "#fde68a" },
  { value: "falta", label: "Falta", short: "F", bg: "rgba(239,68,68,0.25)", border: "var(--red)", color: "#fca5a5" },
  { value: "extra", label: "Día extra (domingo/festivo)", short: "E", bg: "rgba(167,139,250,0.28)", border: "#a78bfa", color: "#c4b5fd" },
];
const CYCLE = [null, "completo", "medio", "falta", "extra"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WEEK = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const isoDate = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
const MONTHS_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const shortD = (s) => `${parseInt(s.slice(8), 10)} ${MONTHS_SHORT[parseInt(s.slice(5, 7), 10) - 1]}`;

export default function WorkdaysTab({ employees, projects = [], canWrite, canPay = false, payrolls = [], onGeneratePay }) {
  const [mode, setMode] = useState("team");
  const active = employees.filter((e) => e.active !== false);
  const [empId, setEmpId] = useState(active[0]?.id || "");
  const today = new Date();
  const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [marks, setMarks] = useState({}); // date -> day_type
  const [summary, setSummary] = useState(null);
  const emp = employees.find((e) => e.id === empId);

  const range = useMemo(() => {
    const last = new Date(ym.y, ym.m + 1, 0).getDate();
    return { start: iso(ym.y, ym.m, 1), end: iso(ym.y, ym.m, last), lastDay: last };
  }, [ym]);

  // Semanas lunes-domingo que tocan el mes mostrado (pueden cruzar de mes)
  const weeks = useMemo(() => {
    const first = new Date(ym.y, ym.m, 1);
    const monday = new Date(ym.y, ym.m, 1 - ((first.getDay() + 6) % 7));
    const lastDay = new Date(ym.y, ym.m + 1, 0);
    const out = [];
    for (let d = new Date(monday); d <= lastDay; d.setDate(d.getDate() + 7)) {
      const ws = new Date(d);
      const we = new Date(d);
      we.setDate(we.getDate() + 6);
      out.push({ start: isoDate(ws), end: isoDate(we) });
    }
    return out;
  }, [ym]);

  const extRange = useMemo(() => ({
    start: weeks[0]?.start || range.start,
    end: weeks[weeks.length - 1]?.end || range.end,
  }), [weeks, range]);

  const load = async () => {
    if (!empId) return;
    const [w, s] = await Promise.all([
      api.get("/workdays", { params: { employee_id: empId, start: extRange.start, end: extRange.end } }),
      api.get("/workdays/summary", { params: { employee_id: empId, start: range.start, end: range.end } }),
    ]);
    const map = {};
    w.data.forEach((r) => { map[r.date] = r.day_type; });
    setMarks(map);
    setSummary(s.data);
  };
  useEffect(() => { load(); }, [empId, range.start]); // eslint-disable-line

  const cycleDay = async (dateStr) => {
    if (!canWrite || !empId) return;
    const cur = marks[dateStr] || null;
    const next = CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length];
    setMarks((m) => ({ ...m, [dateStr]: next }));
    try {
      await api.put("/workdays", { employee_id: empId, date: dateStr, day_type: next });
      api.get("/workdays/summary", { params: { employee_id: empId, start: range.start, end: range.end } }).then(({ data }) => setSummary(data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
      load();
    }
  };

  const firstWeekday = (new Date(ym.y, ym.m, 1).getDay() + 6) % 7; // lunes = 0
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: range.lastDay }, (_, i) => i + 1)];

  const weekStats = (wk) => {
    let paidDays = 0, marked = 0;
    const cur = new Date(wk.start.slice(0, 4), parseInt(wk.start.slice(5, 7), 10) - 1, parseInt(wk.start.slice(8), 10));
    for (let i = 0; i < 7; i++) {
      const t = marks[isoDate(cur)];
      if (t) marked += 1;
      if (t === "completo" || t === "extra") paidDays += 1;
      else if (t === "medio") paidDays += 0.5;
      cur.setDate(cur.getDate() + 1);
    }
    return { paidDays, marked, amount: summary ? Math.round(paidDays * summary.day_value * 100) / 100 : 0 };
  };

  const weekPaidEntry = (wk) =>
    payrolls.find((p) => p.employee_id === empId && p.status !== "cancelado" && p.period_start <= wk.end && p.period_end >= wk.start);

  const payWeek = async (wk) => {
    try {
      const { data } = await api.get("/workdays/summary", { params: { employee_id: empId, start: wk.start, end: wk.end } });
      onGeneratePay?.({ employee_id: empId, period_start: wk.start, period_end: wk.end, gross: data.gross, _summary: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo calcular la semana");
    }
  };

  return (
    <div className="space-y-4" data-testid="workdays-tab">
      <div className="flex gap-1.5" data-testid="workdays-mode-toggle">
        {[["team", "Equipo", Users], ["individual", "Individual", User]].map(([m, l, Icon]) => (
          <button key={m} data-testid={`workdays-mode-${m}`} onClick={() => setMode(m)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] uppercase tracking-widest font-semibold transition-colors"
            style={mode === m ? { background: "var(--cyan)", color: "#0c1424" } : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {mode === "team" ? (
        <TeamWorkdays employees={employees} projects={projects} canWrite={canWrite} canPay={canPay} />
      ) : (
        <>
      <div className="panel p-3 flex flex-wrap items-center gap-3">
        <CalendarDays className="w-4 h-4" style={{ color: "var(--cyan)" }} />
        <select data-testid="workdays-employee" value={empId} onChange={(e) => setEmpId(e.target.value)} className="input-cyber px-3 py-2 text-sm min-w-[240px]">
          {active.length === 0 && <option value="">Sin empleados activos</option>}
          {active.map((e) => (
            <option key={e.id} value={e.id} style={{ background: "#0a0f1d" }}>{`${e.name} — ${e.position} ${e.payroll_type === "obra" ? "(OBRA)" : "(ADMIN)"}`}</option>
          ))}
        </select>
        <div className="flex items-center gap-1 ml-auto">
          <button data-testid="workdays-prev" onClick={() => setYm(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))} className="btn-ghost p-1.5 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-display font-bold text-slate-100 w-44 text-center" data-testid="workdays-month">{MONTHS[ym.m]} {ym.y}</span>
          <button data-testid="workdays-next" onClick={() => setYm(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))} className="btn-ghost p-1.5 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="panel p-4 lg:col-span-2">
          <div className="grid grid-cols-7 gap-1.5 mb-1.5">
            {WEEK.map((d) => <div key={d} className="label-mono text-center text-[9px]" style={{ color: d === "DOM" ? "#a78bfa" : "var(--text-mute)" }}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((d, i) => {
              if (d === null) return <div key={`x${i}`} />;
              const dateStr = iso(ym.y, ym.m, d);
              const t = marks[dateStr];
              const cfg = DAY_TYPES.find((x) => x.value === t);
              return (
                <button key={dateStr} data-testid={`workday-${dateStr}`} onClick={() => cycleDay(dateStr)} disabled={!canWrite}
                  className="aspect-square rounded-sm flex flex-col items-center justify-center transition-colors text-sm font-mono disabled:cursor-default"
                  style={cfg
                    ? { background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color }
                    : { background: "var(--bg-2)", border: "1px solid var(--border-2)", color: "var(--text-dim)" }}
                  title={cfg ? cfg.label : "Sin marcar — clic para marcar"}>
                  <span className="font-bold">{d}</span>
                  {cfg && <span className="text-[10px] font-bold uppercase">{cfg.short}</span>}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-[10px]">
            {DAY_TYPES.map((t) => (
              <span key={t.value} className="flex items-center gap-1.5" style={{ color: "var(--text-dim)" }}>
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: t.bg, border: `1px solid ${t.border}` }} /> {t.label}
              </span>
            ))}
            {canWrite && <span className="ml-auto" style={{ color: "var(--text-mute)" }}>Clic en un día: completo → medio → falta → extra → quitar</span>}
          </div>
        </div>

        <div className="panel p-4 space-y-3" data-testid="workdays-summary">
          <div className="label-mono" style={{ color: "var(--cyan)" }}>// RESUMEN DEL MES</div>
          {emp && summary ? (
            <>
              <div className="text-xs text-slate-400">
                {emp.name} · Salario {fmtMXN(summary.base_salary)} <span className="capitalize">({summary.payment_period})</span>
                <div className="mt-0.5">Valor del día: <b className="font-mono" style={{ color: "var(--cyan)" }}>{fmtMXNFull(summary.day_value)}</b></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SumBox label="DÍAS COMPLETOS" value={summary.completos} color="var(--green)" />
                <SumBox label="MEDIOS DÍAS" value={summary.medios} color="#fbbf24" />
                <SumBox label="DÍAS EXTRA" value={summary.extras} color="#a78bfa" />
                <SumBox label="FALTAS" value={summary.faltas} color="var(--red)" />
              </div>
              <div className="px-3 py-3 rounded-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
                <div className="label-mono text-[9px]">DÍAS PAGADOS: {summary.paid_days}</div>
                <div className="metric-value text-2xl mt-1" style={{ color: "var(--cyan)", textShadow: "0 0 12px var(--cyan-glow)" }} data-testid="workdays-total">
                  {fmtMXNFull(summary.gross)}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">a pagar por lo trabajado en {MONTHS[ym.m]}</div>
              </div>
              <div className="space-y-1.5" data-testid="workdays-weeks">
                <div className="label-mono text-[9px]" style={{ color: "var(--magenta)" }}>// PAGO POR SEMANA (LUN–DOM)</div>
                {weeks.map((wk) => {
                  const st = weekStats(wk);
                  const paid = weekPaidEntry(wk);
                  return (
                    <div key={wk.start} data-testid={`week-row-${wk.start}`} className="flex items-center gap-2 px-2.5 py-2 rounded-sm text-xs"
                      style={{ background: "var(--panel)", border: "1px solid var(--border)", opacity: st.marked === 0 && !paid ? 0.55 : 1 }}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-slate-200">{shortD(wk.start)} – {shortD(wk.end)}</div>
                        <div className="text-[10px] text-slate-500">{st.marked === 0 ? "Sin asistencia marcada" : <>{st.paidDays} día{st.paidDays === 1 ? "" : "s"} · <b className="font-mono" style={{ color: "var(--cyan)" }}>{fmtMXN(st.amount)}</b></>}</div>
                      </div>
                      {paid ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] font-semibold uppercase tracking-widest" data-testid={`week-paid-${wk.start}`}
                          style={{ background: "rgba(34,197,94,0.12)", color: "var(--green)", border: "1px solid rgba(34,197,94,0.35)" }}>
                          <CheckCircle2 className="w-3 h-3" /> {paid.status === "borrador" ? "Generado" : "Pagado"}
                        </span>
                      ) : canPay && onGeneratePay ? (
                        <button onClick={() => payWeek(wk)} disabled={st.marked === 0} data-testid={`week-pay-${wk.start}`}
                          className="btn-primary px-2.5 py-1.5 rounded-sm text-[9px] uppercase tracking-widest font-semibold flex items-center gap-1 disabled:opacity-35 disabled:cursor-not-allowed">
                          <Banknote className="w-3 h-3" /> Generar pago
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-500">Selecciona un empleado.</p>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
}

const SumBox = ({ label, value, color }) => (
  <div className="px-2.5 py-2 rounded-sm" style={{ background: "var(--panel)", border: "1px solid var(--border)" }}>
    <div className="label-mono text-[8px]">{label}</div>
    <div className="font-mono font-bold text-lg" style={{ color }}>{value}</div>
  </div>
);
